import {
  Alert,
  Badge,
  Button,
  CellText,
  DataTable,
  EmptyState,
  Icon,
  KeyValue,
  List,
  ListRow,
  MoreMenu,
  PageHeader,
  Section,
  Skeleton,
  Text,
  useToast,
  type MenuItem,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import {
  useBatch,
  useCancelFilling,
  useDeleteBatch,
  type Batch,
  type Filling,
  type Movement,
} from '../api';
import { BatchFormDialog } from '../components/BatchFormDialog';
import { CorrectionDialog } from '../components/CorrectionDialog';
import { FillingDialog } from '../components/FillingDialog';
import { formatKg, formatPercent, shareOf } from '../format';
import styles from './HarvestPages.module.css';

type Open = 'edit' | 'fill' | 'correct' | 'delete' | null;

/** A batch with traceability: colonies, locations, fillings, ledger (HRV-006). */
export function BatchDetailPage() {
  const { t } = useTranslation();
  const { batchId = '' } = useParams();
  const batch = useBatch(batchId);
  if (batch.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(batch.error)}
        actions={<BackLink to="/ernte" label={t('harvest.title')} />}
      />
    );
  }
  if (!batch.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={8} />
      </div>
    );
  }
  return <BatchDetail batch={batch.data} />;
}

function BatchDetail({ batch }: { batch: Batch }) {
  const { t } = useTranslation();
  const rowMenu = useRowMenu();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState<Open>(null);
  const [cancelling, setCancelling] = useState<Filling | null>(null);
  const remove = useDeleteBatch();
  const cancel = useCancelFilling();
  const close = () => setOpen(null);
  const jars = batch.articles.reduce((s, a) => s + a.jars, 0);
  const jarGrams = batch.articles.reduce((s, a) => s + a.jars * a.net_weight_grams, 0);
  const place =
    batch.location?.name ?? (batch.shares.length > 0 ? t('harvest.batch.mixedShort') : null);

  const more: MenuItem[] = [
    ...(can('harvest.write')
      ? [{ key: 'edit', label: t('common.edit'), onSelect: () => setOpen('edit') }]
      : []),
    ...(can('stock.write')
      ? [
          {
            key: 'correct',
            label: t('harvest.correction.title'),
            onSelect: () => setOpen('correct'),
          },
        ]
      : []),
    ...(can('harvest.write') && !batch.has_stock_activity
      ? [
          {
            key: 'delete',
            label: t('harvest.batch.delete'),
            tone: 'danger' as const,
            separated: true,
            onSelect: () => setOpen('delete'),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        back={<BackLink to="/ernte" label={t('harvest.title')} />}
        title={t('harvest.batch.title', { lot: batch.lot })}
        meta={[
          batch.variety.name,
          t('harvest.batch.eyebrow', { date: formatDate(batch.harvested_on) }),
          place,
          t('harvest.batch.bestBeforeBadge', { date: formatDate(batch.best_before) }),
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {can('stock.write') && (
              <Button
                onClick={() => setOpen('fill')}
                disabled={batch.bulk_grams === 0}
                iconStart={<Icon name="plus" size="sm" />}
              >
                {t('harvest.filling.action')}
              </Button>
            )}
            {more.length > 0 && <MoreMenu label={t('common.moreActions')} items={more} />}
          </>
        }
      />
      {batch.water_warning && batch.water_content != null && (
        <Alert tone="warning" title={t('harvest.water.warningTitle')}>
          {t('harvest.water.measured', { value: formatPercent(batch.water_content) })}{' '}
          {t('harvest.water.warning')}
        </Alert>
      )}

      <div className={styles.columns}>
        <div className={styles.main}>
          <Section title={t('harvest.shares.title')}>
            {batch.shares.length === 0 ? (
              <Text tone="muted" className={styles.empty}>
                {t('harvest.shares.noneDetail')}
              </Text>
            ) : (
              <List>
                {batch.shares.map((s) => (
                  <ListRow
                    key={s.colony.id}
                    href={`/voelker/${s.colony.id}`}
                    title={s.colony.name}
                    description={s.location?.name}
                    trailing={`${formatKg(s.grams)} · ${shareOf(s.grams, batch.weight_grams)} %`}
                  />
                ))}
              </List>
            )}
          </Section>

          <Section title={t('harvest.fillings.title')}>
            {batch.fillings.length === 0 ? (
              <Text tone="muted" className={styles.empty}>
                {t('harvest.fillings.emptyTitle')}
              </Text>
            ) : (
              <List>
                {batch.fillings.map((f) => (
                  <ListRow
                    key={f.id}
                    title={f.lines.map((l) => `${l.count} × ${l.article.name}`).join(', ')}
                    description={[
                      formatDate(f.filled_on),
                      f.loss_grams > 0
                        ? t('harvest.fillings.loss', { amount: formatKg(f.loss_grams) })
                        : null,
                      f.created_by,
                      f.notes,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    trailing={
                      f.cancelled && <Badge size="sm">{t('harvest.fillings.cancelled')}</Badge>
                    }
                    actionsLabel={rowMenu.label(formatDate(f.filled_on))}
                    actions={
                      !f.cancelled && can('stock.write')
                        ? [rowMenu.remove(() => setCancelling(f), t('harvest.fillings.cancel'))]
                        : []
                    }
                  />
                ))}
              </List>
            )}
          </Section>

          <Section title={t('harvest.ledger.title')} variant="flush">
            <DataTable
              caption={t('harvest.ledger.title')}
              rows={batch.movements}
              rowKey={(m) => m.id}
              columns={[
                {
                  key: 'date',
                  header: t('harvest.ledger.date'),
                  cell: (m) => formatDate(m.booked_on),
                },
                {
                  key: 'kind',
                  header: t('harvest.ledger.kind'),
                  primary: true,
                  cell: (m: Movement) => (
                    <CellText
                      main={t(`harvest.movement.${m.kind}`)}
                      sub={[m.reason ? t(`harvest.reason.${m.reason}`) : null, m.note]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  ),
                },
                {
                  key: 'what',
                  header: t('harvest.ledger.what'),
                  cell: (m) => m.article?.name ?? t('harvest.ledger.bulk'),
                },
                {
                  key: 'change',
                  header: t('harvest.ledger.change'),
                  align: 'end',
                  cell: (m) => <Change m={m} />,
                },
                {
                  key: 'by',
                  header: t('harvest.ledger.by'),
                  cell: (m) => m.created_by ?? '–',
                },
              ]}
              empty={
                <Text tone="muted" className={styles.tableEmpty}>
                  {t('harvest.ledger.empty')}
                </Text>
              }
            />
          </Section>
        </div>

        <div className={styles.side}>
          <Section
            title={t('harvest.batch.details')}
            footer={
              batch.changed.at &&
              (batch.changed.by
                ? t('apiary.colony.changed', {
                    date: formatDateTime(batch.changed.at),
                    name: batch.changed.by,
                  })
                : t('apiary.colony.changedNoName', { date: formatDateTime(batch.changed.at) }))
            }
          >
            <KeyValue
              items={[
                {
                  key: 'weight',
                  label: t('harvest.stats.weight'),
                  value: formatKg(batch.weight_grams),
                },
                { key: 'bulk', label: t('harvest.stats.bulk'), value: formatKg(batch.bulk_grams) },
                {
                  key: 'jars',
                  label: t('harvest.stats.jars'),
                  value:
                    jars > 0
                      ? `${t('harvest.articles.count', { count: jars })} · ${formatKg(jarGrams)}`
                      : null,
                },
                {
                  key: 'water',
                  label: t('harvest.stats.water'),
                  value: batch.water_content != null ? formatPercent(batch.water_content) : null,
                },
                { key: 'lot', label: t('harvest.batch.lot'), value: batch.lot },
                {
                  key: 'harvested',
                  label: t('harvest.batch.harvestedOn'),
                  value: formatDate(batch.harvested_on),
                },
                {
                  key: 'bestBefore',
                  label: t('harvest.batch.bestBefore'),
                  value: formatDate(batch.best_before),
                },
                { key: 'location', label: t('harvest.batch.location'), value: place },
                {
                  key: 'notes',
                  label: t('harvest.batch.notes'),
                  value: batch.notes && <span className={styles.notes}>{batch.notes}</span>,
                },
              ]}
            />
          </Section>

          {batch.articles.length > 0 && (
            <Section title={t('harvest.articles.title')}>
              <List>
                {batch.articles.map((a) => (
                  <ListRow
                    key={a.article.id}
                    title={a.article.name}
                    trailing={t('harvest.articles.count', { count: a.jars })}
                  />
                ))}
              </List>
            </Section>
          )}

          {batch.sales.length > 0 && (
            <Section title={t('harvest.sales.title')}>
              <List>
                {batch.sales.map((s) => (
                  <ListRow
                    key={`${s.sale_id}-${s.article.id}`}
                    {...(can('sales.read') ? { href: `/verkauf/${s.sale_id}` } : {})}
                    title={s.customer ?? s.market_day ?? t('harvest.sales.anonymous')}
                    description={`${formatDate(s.on)} · ${s.article.name}`}
                    trailing={
                      <>
                        {s.cancelled && <Badge size="sm">{t('harvest.sales.cancelled')}</Badge>}
                        {t('harvest.articles.count', { count: s.jars })}
                      </>
                    }
                  />
                ))}
              </List>
            </Section>
          )}
        </div>
      </div>

      {open === 'edit' && <BatchFormDialog open batch={batch} onClose={close} />}
      {open === 'fill' && <FillingDialog open batchId={batch.id} onClose={close} />}
      {open === 'correct' && <CorrectionDialog open batch={batch} onClose={close} />}
      <ConfirmDialog
        open={open === 'delete'}
        onClose={() => {
          remove.reset();
          close();
        }}
        title={t('harvest.batch.deleteTitle', { lot: batch.lot })}
        description={t('harvest.batch.deleteText')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          remove.mutate(batch.id, {
            onSuccess: () => {
              toast.show(t('harvest.batch.deleted', { lot: batch.lot }));
              void navigate('/ernte');
            },
          })
        }
      />
      <ConfirmDialog
        open={cancelling !== null}
        onClose={() => {
          cancel.reset();
          setCancelling(null);
        }}
        title={t('harvest.fillings.cancelTitle')}
        description={t('harvest.fillings.cancelText')}
        confirmLabel={t('harvest.fillings.cancel')}
        pending={cancel.isPending}
        error={cancel.error ? errorMessage(cancel.error) : undefined}
        onConfirm={() =>
          cancelling &&
          cancel.mutate(
            { batchId: batch.id, fillingId: cancelling.id },
            {
              onSuccess: () => {
                toast.show(t('harvest.fillings.cancelled'));
                setCancelling(null);
              },
            },
          )
        }
      />
    </div>
  );
}

/** A ledger change with its sign, e.g. "−2 Gläser" or "+30 kg". */
function Change({ m }: { m: Movement }) {
  const { t } = useTranslation();
  const value = m.article ? m.jars : m.bulk_grams;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const text = m.article
    ? t('harvest.articles.count', { count: Math.abs(value) })
    : formatKg(Math.abs(value), 3);
  return (
    <span className={styles.num}>
      {sign}
      {text}
    </span>
  );
}
